// src/oura/components/newscan/steps/BusinessStep.tsx
//
// Step 2, "Check what we found": the details the 50 are drafted
// from, as the website read suggested them, for staff to check.
//
// Two ways in:
// - A new business before its draft exists: the step edits the flow's form
//   (setForm) and the foot drafts the 50 (createDraft). An outlet staff added
//   that the read never found asks first ("<Area> is not on the website").
// - An outlet named after its own town ("Subang Jaya" in Subang Jaya) asks for
//   the part of town it is in, before drafting and in a draft alike
//   (AnchorTownCard; city_rule anchor_is_not_a_town).
// - A draft that exists (an edit, a recipe update, or a new scan after its
//   draft): every change is written at once (ctl.patchBusiness). When the
//   change feeds the 50, the draft goes stale and the foot offers
//   "Update the 50"; otherwise "Back to review".
//
// Every edit is in place: no dialogs. Text fields confirm on Enter or when
// left, Esc puts the old value back.
//
// A First AI check (JOURNEY-SPEC v1.1 §2.5): every outlet and service says
// where it came from (the site, the client's form, the pitch, the quote, or
// staff), the sentence names the pitch, the quote and the form, and Search
// Console is not offered (the access request comes after this check). On
// every purpose, "Notes and files for the research" lists the business's
// Knowledge and stages more (KnowledgeFold).

import { ArrowRightIcon, GlobeIcon, HomeIcon, LayersIcon, Loader2Icon, LockIcon, PencilIcon, RefreshCwIcon, StoreIcon, TriangleAlertIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { scanDraftModeFor } from "@service/aeo/scanDraftTypes";
import { useUserProfileContext } from "@context/user_profile";
import type { BuyMode, ScanDraft, ScanDraftBusiness, ScanDraftDetected, ScanDraftFailure, ScanDraftMarket, ScanDraftOutlet } from "@service/aeo/scanDraftTypes";
import { cn } from "@oura/lib/cn";
import { DrawerFold, PillButton, Surface, TYPE } from "@oura/ui";
import { Refusal } from "@oura/components/onboarding/fields";
import { BUY_LABEL, FRAME, plural } from "@oura/newscan/copy";
import { useScanFlow } from "@oura/newscan/ScanFlowContext";
import { StepHead } from "@oura/components/newscan/StepHead";
import { RadioSeg } from "@oura/components/newscan/RadioSeg";
import { TagInput } from "@oura/components/newscan/TagInput";
import { DecisionCard } from "@oura/components/newscan/DecisionCard";
import { Muted } from "@oura/components/newscan/Muted";
import { OutletList } from "@oura/components/newscan/OutletList";
import { AnchorTownCard } from "@oura/components/newscan/AnchorTownCard";
import { anchorTown } from "@oura/newscan/towns";
import { takeOutletIntent } from "@oura/newscan/outletIntent";
import { MoreOptions } from "@oura/components/newscan/MoreOptions";
import { MarketList } from "@oura/components/newscan/MarketList";
import { marketFromName, marketOf } from "@oura/newscan/markets";
import { DefRow } from "@oura/components/newscan/DefRow";
import { InlineText } from "@oura/components/newscan/InlineText";
import { KnowledgeFold } from "@oura/components/newscan/KnowledgeFold";
import { ITEM_SOURCE } from "@oura/components/newscan/itemSource";
import { firstNameOf, formThingsOpenTitles, isFirstCheck, pitchDay } from "@oura/components/newscan/firstCheckFacts";
import { getOnboarding } from "@service/aeo/onboarding";
import { isLocationMode, mergeIntoFirst, sameVenueCandidates, withoutAreaNames } from "@oura/newscan/singleVenue";
import { LOCATION_TOTAL, SINGLE_LOCATION_TOTAL, outletShares, shapeFromBusiness, shapeFromDraft } from "@oura/newscan/recipeShape";

/* ------------------------------------------------------------------------- *
 * Copy
 * ------------------------------------------------------------------------- */

const COPY = {
    title: "Check what we found",
    readFrom: (domain: string, gsc: boolean) => `Read from ${domain}${gsc ? " and Search Console" : ""}. `,
    /** "2 things to check:", the list of them follows under the sentence. */
    toCheckList: (n: number) => `${plural(n, "thing")} to check.`,
    mismatchTitle: "Doesn't match",
    mismatchFirst: (who: string) => `Oura compared the site, the quote and ${who}'s answers. Fix these below, or leave them if they are right.`,
    mismatchSite: "Oura compared what you added with the site. Fix these below, or leave them if they are right.",
    unconfirmedItem: (area: string) => `${area} is not on the website`,
    anchorItem: (area: string) => `${area} is also its town`,
    // First AI check (JOURNEY-SPEC §2.5).
    fromSigning: (day: string | null, contact: string) =>
        day ? `From the pitch on ${day}, the quote and ${contact}'s answers. ` : `From the site, the quote and ${contact}'s answers. `,
    matchesPitch: "Everything matches the pitch. Change anything that is not right.",
    nothingToCheck: "Nothing to check. Change anything that is not right.",
    theClient: "the client",
    allRight: "Everything looks right. Change anything that is not.",
    lowRead: "Not much to read there. Fill in the details below.",
    editSentence: "The details the 50 are drafted from. Change anything that is not right.",
    staleSentence: "Changed since the 50 were drafted. Update the 50 to use it.",
    building: "Locked while Oura drafts the 50, usually about a minute. You can read everything here meanwhile.",
    frozen: "The 50 are approved, so the business is locked. Reopen them to change it.",
    reopen: "Reopen the 50",
    started: "These 50 started the check, so the business is locked.",
    business: "Business",
    name: "Business name",
    category: "Category",
    addName: "Add the business name",
    addCategory: "add the category",
    customers: "How customers buy",
    suggested: (why: string) => `Suggested: the site has ${why.replace(/\.$/, "")}.`,
    changed: (label: string) => `Changed from the suggestion (${label}).`,
    outlets: "Outlets",
    areas: "Areas served",
    location: "Location",
    locationHint: "Found by place: location questions for this one place, under each of its area names.",
    sameVenue: (names: string, town: string) => `Same place? ${names} ${names.includes(" and ") ? "are" : "is"} in ${town}, like the main location.`,
    sameVenueMerge: "Same place, merge",
    sameVenueKeep: "Separate venues",
    areasHint: "Address questions become home-visit questions.",
    sells: "Sells",
    sellsHint: "In the site's words. The draft turns them into the words customers type.",
    sellsPlaceholder: (online: boolean) => (online ? "Add a product, press Enter" : "Add a service, press Enter"),
    recipe: "Recipe",
    buyHint: {
        visit: "Found by place: every outlet gets location questions.",
        online: "No outlet a customer visits: an online store, a product or ingredient brand, a supplier to businesses, software.",
        both: "Found by place: the showroom gets the location questions.",
        we_go_to_them: "Found by place: the areas served get the location questions.",
    } as Record<BuyMode, string>,
    markets: "Markets",
    toBusinesses: "Also sells to businesses",
    yes: "Yes",
    no: "No",
    toBusinessesHint: (yes: boolean) =>
        yes ? "8 questions ask as a business buyer looking for a supplier." : "Sells only to shoppers. 8 more questions ask about buyer problems.",
    partners: "Partner products that carry you",
    partnersPlaceholder: "Add a product, press Enter",
    partnersHint: "Other brands' products made with yours. AI naming one counts as a win. They change the counting, not the questions.",
    doorTitle: (place: string, showroom: boolean) => `The site lists ${showroom ? "a showroom" : "an outlet"} in ${place}`,
    doorDetail: (label: string, showroom: boolean) =>
        `A business with a door is found by place. ${label} asks the ${showroom ? "showroom" : "outlet"} questions${showroom ? ", and its products sit in the other questions" : ""}.`,
    doorKeep: "Keep Online: no one visits it",
    noDoorTitle: "No address customers visit on the site",
    noDoorDetail: "Online asks what buyers search for, market by market, instead of the outlet questions.",
    noDoorKeep: (label: string) => `Keep ${label}: customers visit`,
    takeIt: (label: string) => `Use ${label}`,
    outletTitle: (area: string) => `${area} is not on the website`,
    outletDetail: (k: number | null, flagship: string, rest: number[]) =>
        `You added it, and the site never names it. Keep it if it is open: it gets its own ${k === null ? "" : `${k} `}questions. ` +
        (rest.length <= 1
            ? `Leave it out and ${flagship} takes 14, with more city-wide questions.`
            : `Leave it out and the location questions split ${rest.join("/")} across the other ${rest.length}.`),
    keep: "It is open, keep it",
    leaveOut: "Leave it out",
    saidKept: (area: string) => `Kept ${area}.`,
    saidLeft: (area: string) => `Left out ${area}.`,
    saidRest: (n: number) => (n ? `${plural(n, "thing")} to check.` : "Nothing left to check."),
    saidArea: (sub: string, town: string) => `${sub} is the outlet's area, in ${town}.`,
    saidRing: (area: string, ring: string) => `City-wide questions for ${area} now ask ${ring}.`,
    saidNoSmaller: (area: string) => `${area} stays the outlet's area.`,
    recipePending: "The recipe update is still open. Answer it on the Review step before updating the 50.",
    whyNotLocal: "Why not the local questions",
    whyNotNote: "It would ask about a shop it does not run",
    foot: {
        cost: "No engines asked. About a minute, cents.",
        answerFirst: "Answer the question above first",
        nameFirst: "Add the business name first",
        outletFirst: (showroom: boolean) => (showroom ? "Add a showroom first" : "Add an outlet first"),
        sellsFirst: (online: boolean) => (online ? "Add a product first" : "Add a service first"),
        marketFirst: "Add at least 1 market first",
        draft: "Draft the 50",
        update: "Update the 50",
        backToReview: "Back to review",
        looksRight: "Looks right, go to Review",
    },
} as const;

const BUY_ICON: Record<BuyMode, typeof StoreIcon> = {
    visit: StoreIcon,
    online: GlobeIcon,
    both: LayersIcon,
    we_go_to_them: HomeIcon,
};
const BUY_ORDER: BuyMode[] = ["visit", "online", "both", "we_go_to_them"];

// Labels only. Local v4.5 is the v4.2 problem-led local questions unchanged (spec: modes.local is
// v4.2 unchanged); its stamp stays visibility-set-v4.2-problem-led, the stable identifier that
// matches the backend's LOCAL_STAMP; only the label changed (#4317, ourteam-backend#6066).
const RECIPE_LABEL: Record<BuyMode, string> = {
    visit: "Local v4.5",
    we_go_to_them: "We go to them v1",
    online: "Brand v4.5",
    // A showroom is an outlet: Both uses the Local v4.5 recipe.
    both: "Local v4.5",
};

/* ------------------------------------------------------------------------- *
 * Brand mode (Online): markets, business buyers, partner products
 * ------------------------------------------------------------------------- */

/** Brand rows: 28 recommend (36 for shoppers only), 14 cite, 8 business buyers (0 for shoppers only). */
const BRAND = { pick: 28, source: 14, supplier: 8 } as const;

function brandShapeText(pick: number, source: number, supplier: number): string {
    return [
        `${pick} about being recommended`,
        `${source} about your site as the source`,
        ...(supplier > 0 ? [`${supplier} about business buyers`] : []),
    ].join(" · ");
}

/** The brand shape before a draft exists. */
function brandShapeFromBusiness(b: ScanDraftBusiness): string {
    return b.sellsToBusinesses
        ? brandShapeText(BRAND.pick, BRAND.source, BRAND.supplier)
        : brandShapeText(BRAND.pick + BRAND.supplier, BRAND.source, 0);
}

/** The brand shape of a drafted 50, from its counts; null when the counts carry no brand keys. */
function brandShapeFromDraft(d: ScanDraft): string | null {
    const { pick, source, supplier } = d.counts.check;
    if (pick === undefined || source === undefined) return null;
    return brandShapeText(pick, source, supplier ?? 0);
}

/** The brand fields as the read suggests them: markets home last, else the home market alone. */
function brandFromDetected(d: ScanDraftDetected | null, b: ScanDraftBusiness): Partial<ScanDraftBusiness> {
    const guess = (d?.marketsGuess ?? []).filter((m) => marketOf(m.code));
    const home = marketFromName(b.market ?? d?.market ?? null);
    const markets: ScanDraftMarket[] = guess.length ? guess.map((m) => ({ code: m.code, label: marketOf(m.code)?.label ?? m.label })) : home ? [home] : [];
    return {
        markets,
        sellsToBusinesses: d?.sellsToBusinessesGuess ?? false,
        partnerProducts: d?.partnerProductsGuess ?? [],
    };
}

/** The buy answer staff kept against the read, per site: survives the step remounting (a resize, Back and on). */
const keptAgainstRead = new Map<string, BuyMode>();

/** What the brand fields need filled before Online is drafted: the ones not set yet. */
function missingBrandFields(b: ScanDraftBusiness, d: ScanDraftDetected | null): Partial<ScanDraftBusiness> {
    const seed = brandFromDetected(d, b);
    const patch: Partial<ScanDraftBusiness> = {};
    if (b.markets === undefined) patch.markets = seed.markets;
    if (b.sellsToBusinesses === undefined) patch.sellsToBusinesses = seed.sellsToBusinesses;
    if (b.partnerProducts === undefined) patch.partnerProducts = seed.partnerProducts;
    return patch;
}

/* ------------------------------------------------------------------------- *
 * What leaving an outlet out does to the split
 * ------------------------------------------------------------------------- */

/** What the "Leave it out" line says: the share it would get, and the split without it. */
function outletStakes(outlets: ScanDraftOutlet[], index: number): { k: number | null; rest: number[] } {
    const withIt = outlets.filter((o, i) => i === index || (o.name.trim() && (o.source === "site" || o.confirmed === true)));
    const at = withIt.indexOf(outlets[index]);
    const shares = outletShares(withIt.length, withIt.length === 1 ? SINGLE_LOCATION_TOTAL : LOCATION_TOTAL);
    const restCount = withIt.length - 1;
    const rest = restCount <= 1 ? [SINGLE_LOCATION_TOTAL] : outletShares(restCount, LOCATION_TOTAL);
    const k = shares[at];
    return { k: k && k > 0 ? k : null, rest };
}

/** What the local recipe would ask an online store: the shop-shaped questions it cannot answer. */
function todaysRecipeOnStore(b: ScanDraftBusiness): { q: string; why: string }[] {
    const noun = (b.category.trim() || "shop").toLowerCase();
    const city = b.city.trim() || b.outlets[0]?.town?.trim() || "";
    const market = b.market?.trim() || b.markets?.[b.markets.length - 1]?.label || "";
    const town = city || market || "the city";
    const outlet = b.outlets[0]?.name.trim() || "";
    const area = outlet || town;
    const thing = (b.services[0]?.name.trim() || noun).toLowerCase();
    // No city and no outlet: only a country is known, so the shop row names the country and the
    // transit row is left out (an LRT stop needs a real city).
    const placeKnown = Boolean(city || outlet);
    const rows = [
        { q: placeKnown ? `Is there a ${noun} in ${area}?` : `Is there a ${noun} shop in ${town}?`, why: "asks for a shop it does not run" },
        ...(placeKnown ? [{ q: `${thing} near ${area} LRT`, why: "transit means nothing online" }] : []),
        { q: `${noun} in ${town} open on Sunday`, why: "opening hours do not apply" },
        { q: `Which ${noun} in ${town} takes walk-ins without an appointment?`, why: "walk-ins do not apply" },
        { q: `Which ${noun} in ${town} takes same-day appointments?`, why: "appointments do not apply" },
    ];
    return rows.map((r) => ({ ...r, q: capital(r.q) }));
}

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** A lone place has nothing to leave it out for: a staff one counts as confirmed. */
function confirmLone(outlets: ScanDraftOutlet[]): ScanDraftOutlet[] {
    const only = outlets.length === 1 ? outlets[0] : null;
    return only && only.source === "staff" && only.confirmed !== true ? [{ ...only, confirmed: true }] : outlets;
}

/**
 * The places set aside when Online is picked (Online carries none: the contract says
 * "Online: empty"). Kept in the tab's session under the draft, not in a ref, so leaving
 * the step or reloading does not lose what staff added, confirmed or renamed.
 */
// v2: v1 kept one global entry for every pre-draft site, which is never read again.
const PARKED_KEY = "oura.newscan.parked.v2";

/**
 * Per signed-in user and workspace (the tab's session outlives a logout) and per site or draft:
 * another website started in the same tab never finds this one's places. No key until the
 * profile and the entity are known.
 */
function parkedKey(storageScope: string, entity: string): string | null {
    if (!storageScope || !entity) return null;
    return `${PARKED_KEY}.${encodeURIComponent(storageScope)}.${encodeURIComponent(entity)}`;
}

function readParked(key: string | null): ScanDraftOutlet[] | null {
    if (!key) return null;
    try {
        const raw = window.sessionStorage.getItem(key);
        const value = raw ? (JSON.parse(raw) as unknown) : null;
        return Array.isArray(value) ? (value as ScanDraftOutlet[]) : null;
    } catch {
        return null;
    }
}

/**
 * An empty list is written too, as "the places were removed on purpose": the read treats a missing
 * key as "nothing was set aside" and falls back to an older site backup, then the read's own places,
 * which would bring back what staff removed.
 */
function writeParked(key: string | null, outlets: ScanDraftOutlet[]): void {
    if (!key) return;
    try {
        window.sessionStorage.setItem(key, JSON.stringify(outlets));
    } catch {
        // Storage unavailable: the places simply are not remembered across a reload.
    }
}

/* ------------------------------------------------------------------------- *
 * The step
 * ------------------------------------------------------------------------- */

export default function BusinessStep() {
    const flow = useScanFlow();
    const { ctl, kind, foot, goTo, site, form, setForm, createDraft, creating, openDraft } = flow;
    const draft = ctl.draft;
    const inDraft = draft !== null;
    const first = isFirstCheck(draft);

    // A First AI check: how many items the client's form filed to Knowledge (the fold's head).
    const [formItems, setFormItems] = useState<number | null>(null);
    const knowledgeOf = first ? flow.businessId : null;
    useEffect(() => {
        if (!knowledgeOf) return;
        let live = true;
        getOnboarding(knowledgeOf)
            .then((o) => {
                if (live) setFormItems(o?.form.knowledgeItems ?? null);
            })
            .catch(() => undefined);
        return () => {
            live = false;
        };
    }, [knowledgeOf]);

    // A draft's business, shown with the writes that are still on their way.
    const [optimistic, setOptimistic] = useState<ScanDraftBusiness | null>(null);
    const inflight = useRef(0);
    const [failure, setFailure] = useState<ScanDraftFailure | null>(null);

    const business: ScanDraftBusiness | null = inDraft ? optimistic ?? draft.business : form;
    const editable = !inDraft || draft.status === "ready" || draft.status === "failed";
    // A First AI check's draft: the step is two panels from lg up (the page widens for it).
    // An edit of the questions (Edit and rescan) is the same review as the first check: same layout.
    const split = inDraft && (first || draft.kind === "edit_set");

    // Places set aside when Online is picked, so going back brings them back.
    const [, userInfo] = useUserProfileContext();
    const storageScope = userInfo?.id ? `${userInfo.id}:${userInfo.company_id ?? ""}` : "";
    // Before a draft the places belong to the site being drafted; once it exists, to the draft
    // (and Business can still find what was set aside under the site before the draft was made).
    const siteDomain = draft?.domain ?? site.normalized;
    const siteEntity = siteDomain ? `site.${siteDomain}` : "";
    const parkedEntity = draft ? `draft.${draft.id}` : siteEntity;
    const siteParkedAt = parkedKey(storageScope, siteEntity);
    const parkedAt = parkedKey(storageScope, parkedEntity);
    // Online picked before the profile arrives has no key to write to yet: the places wait here, with the
    // site or draft they belong to, and are written the moment the key exists. They are dropped if that
    // site or draft is no longer the one on screen, so they never land under another prospect's key.
    const waitingParked = useRef<{ entity: string; outlets: ScanDraftOutlet[] } | null>(null);
    // A draft made from the site may still take over the places set aside under that site.
    const waitingIsHere = (waiting: { entity: string } | null) => !!waiting && (waiting.entity === parkedEntity || (!!draft && waiting.entity === siteEntity));
    useEffect(() => {
        const waiting = waitingParked.current;
        if (!waiting) return;
        if (!waitingIsHere(waiting)) {
            waitingParked.current = null;
            return;
        }
        if (!storageScope) return;
        writeParked(parkedKey(storageScope, waiting.entity), waiting.outlets);
        waitingParked.current = null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageScope, parkedEntity, siteEntity, draft?.id]);

    const update = useCallback(
        (patch: Partial<ScanDraftBusiness>) => {
            if (!business) return;
            const next = { ...business, ...patch };
            setFailure(null);
            if (!inDraft) {
                setForm(next);
                return;
            }
            setOptimistic(next);
            inflight.current += 1;
            void ctl.patchBusiness(patch).then((res) => {
                inflight.current -= 1;
                if (res && res.kind !== "stale") setFailure(res);
                // The draft now holds every write: show it as it is.
                if (inflight.current === 0) setOptimistic(null);
            });
        },
        [business, inDraft, setForm, ctl],
    );

    /* ---- what the read suggested ---- */
    const detected = site.detected;
    const suggest = !inDraft || kind === "new_scan" ? detected?.buyGuess ?? null : null;
    // The read's brand guesses (business buyers) are marked only when it read the site as Online.
    const suggestBrand = suggest === "online";

    const setBuy = (buy: BuyMode) => {
        if (!business || buy === business.buy) return;
        const patch: Partial<ScanDraftBusiness> = { buy, mode: scanDraftModeFor(buy) };
        if (buy === "online") {
            if (parkedAt) writeParked(parkedAt, business.outlets);
            else if (parkedEntity) waitingParked.current = { entity: parkedEntity, outlets: business.outlets };
            patch.outlets = [];
            // Markets, business buyers and partner products as the read suggests, unless already set.
            // Switching away keeps them in the form, so switching back loses nothing.
            Object.assign(patch, missingBrandFields(business, detected));
            const home = (patch.markets ?? business.markets ?? []).slice(-1)[0];
            if (home) patch.market = home.label;
        } else if (business.buy === "online") {
            const waitingBack = waitingIsHere(waitingParked.current) ? waitingParked.current!.outlets : null;
            const back = waitingBack ?? readParked(parkedAt) ?? (draft ? readParked(siteParkedAt) : null) ?? detected?.outlets.map((o) => ({ name: o.name, town: o.town || business.city, source: "site" as const })) ?? [];
            waitingParked.current = null;
            // The backup stays until the next Online pick replaces it: kept, it is safe if this write fails.
            patch.outlets = back;
        }
        if (buy === "online" && !patch.market && !business.market) patch.market = detected?.market ?? "Malaysia";
        // Other area names belong to a visited place: leaving Visit us drops them, so no hidden names are sent.
        const places = patch.outlets ?? business.outlets;
        if (buy !== "visit" && places.some((o) => o.alsoSearchedAs)) patch.outlets = places.map(withoutAreaNames);
        update(patch);
    };

    const setOutlets = (outlets: ScanDraftOutlet[]) => update({ outlets: confirmLone(outlets) });
    /** The outlet list staff answered "Separate venues" for. */
    const [keptSeparate, setKeptSeparate] = useState<string | null>(null);

    const brand = business?.buy === "online";

    // A form the read seeded as Online gets its brand fields from the read, once.
    useEffect(() => {
        if (inDraft || !form || form.buy !== "online") return;
        const patch = missingBrandFields(form, detected);
        if (form.mode !== "brand") patch.mode = "brand";
        if (Object.keys(patch).length) setForm({ ...form, ...patch });
    }, [inDraft, form, detected, setForm]);

    const setMarkets = (markets: ScanDraftMarket[]) => {
        const home = markets[markets.length - 1];
        update({ markets, ...(home ? { market: home.label } : {}) });
    };

    // The drafted 50 say their own shape; a change not drafted yet shows the shape it would give.
    const drafted = inDraft && !optimistic && !draft.stale && draft.questions.length > 0;
    const shape = !business
        ? ""
        : brand
          ? (drafted && draft.recipe.shape === "online" ? brandShapeFromDraft(draft) : null) ?? brandShapeFromBusiness(business)
          : drafted
            ? shapeFromDraft(draft)
            : shapeFromBusiness(business);

    /* ---- the read and the buy answer disagree (before the draft only; the server asks after) ---- */
    // The answer kept against the read belongs to the site it was given for, never to the next one shown.
    const [keptBuy, setKeptBuyState] = useState<{ site: string; buy: BuyMode } | null>(() => {
        const buy = keptAgainstRead.get(site.normalized);
        return buy ? { site: site.normalized, buy } : null;
    });
    const keptBuyForSite = keptBuy?.site === site.normalized ? keptBuy.buy : keptAgainstRead.get(site.normalized) ?? null;
    const setKeptBuy = (buy: BuyMode) => {
        keptAgainstRead.set(site.normalized, buy);
        setKeptBuyState({ site: site.normalized, buy });
    };
    const readDoor = detected?.outlets.find((o) => o.name.trim()) ?? null;
    const doorGuess: BuyMode | null = detected?.buyGuess === "visit" || detected?.buyGuess === "both" ? detected.buyGuess : readDoor ? "both" : null;
    const mismatch: { kind: "door"; to: BuyMode; place: string } | { kind: "no_door"; from: BuyMode } | null =
        inDraft || !business || !editable || keptBuyForSite === business.buy
            ? null
            : business.buy === "online" && readDoor && doorGuess
              ? { kind: "door", to: doorGuess, place: readDoor.name.trim() }
              : (business.buy === "visit" || business.buy === "both") && detected?.buyGuess === "online" && !readDoor
                ? { kind: "no_door", from: business.buy }
                : null;
    const hasMismatch = mismatch !== null;
    const answerMismatch = (buy: BuyMode | null) => {
        if (!business) return;
        if (buy) setBuy(buy);
        else setKeptBuy(business.buy);
        // The card is gone: focus the Customers choice, which says what is picked now.
        requestAnimationFrame(() => document.querySelector<HTMLElement>("[aria-labelledby='newscan-customers'] [aria-checked='true']")?.focus({ preventScroll: true }));
    };

    // "Name the area" from a First AI check card: the outlet field opens with the form's area in it.
    const [outletIntent] = useState(takeOutletIntent);

    /* ---- staff outlets the read never found (before the draft only) ---- */
    const unconfirmed = useMemo(
        () => (inDraft || !business ? [] : business.outlets.map((o, i) => ({ o, i })).filter(({ o }) => o.source === "staff" && o.confirmed !== true && business.buy !== "online")),
        [inDraft, business],
    );

    const [said, setSaid] = useState("");
    const answerOutlet = (i: number, keep: boolean) => {
        if (!business) return;
        const area = business.outlets[i]?.name ?? "";
        const next = keep
            ? business.outlets.map((x, j) => (j === i ? { ...x, confirmed: true } : x))
            : confirmLone(business.outlets.filter((_, j) => j !== i));
        update({ outlets: next });
        const left = next.filter((o) => o.source === "staff" && o.confirmed !== true).length;
        const line = `${keep ? COPY.saidKept(area) : COPY.saidLeft(area)} ${COPY.saidRest(left)}`;
        setSaid((was) => (was === line ? `${line}\u200b` : line));
        // The answered card is gone: focus the next card, else the places list, else the step title.
        requestAnimationFrame(() => {
            const target =
                document.querySelector<HTMLElement>("[data-outlet-card] button") ??
                document.querySelector<HTMLElement>("[data-outlets-focus] button") ??
                document.querySelector<HTMLElement>("[data-step-title]");
            target?.focus({ preventScroll: true });
        });
    };

    /* ---- an outlet named after its own town (before the draft and in a draft) ---- */
    const townAnchors = useMemo(
        () =>
            !business || business.buy === "online"
                ? []
                : business.outlets.flatMap((o, i) => {
                      const town = o.name.trim() ? anchorTown(o, business.city) : null;
                      return town ? [{ o, i, town }] : [];
                  }),
        [business],
    );

    const afterAnchor = (line: string) => {
        setSaid((was) => (was === line ? `${line}\u200b` : line));
        // The answered card is gone: focus the next card, else the places list, else the step title.
        requestAnimationFrame(() => {
            const target =
                document.querySelector<HTMLElement>("[data-anchor-card] input, [data-anchor-card] button") ??
                document.querySelector<HTMLElement>("[data-outlet-card] button") ??
                document.querySelector<HTMLElement>("[data-outlets-focus] button") ??
                document.querySelector<HTMLElement>("[data-step-title]");
            target?.focus({ preventScroll: true });
        });
    };

    /** "Use this area": the outlet takes the sub-area and keeps the town. */
    const takeSubArea = (i: number, town: string, sub: string) => {
        if (!business) return;
        const next = business.outlets.map((o, j) => {
            if (j !== i) return o;
            const renamed: ScanDraftOutlet = { ...o, name: sub, town };
            delete renamed.noSmallerArea;
            return renamed;
        });
        update({ outlets: next });
        afterAnchor(COPY.saidArea(sub, town));
    };

    /** "It has no smaller area": the name stays, the city-wide questions ask the wider ring (when there is one). */
    const keepTownArea = (i: number, ring: string | null) => {
        if (!business) return;
        const area = business.outlets[i]?.name.trim() ?? "";
        update({ outlets: business.outlets.map((o, j) => (j === i ? { ...o, town: ring ?? o.town, noSmallerArea: true } : o)) });
        afterAnchor(ring ? COPY.saidRing(area, ring) : COPY.saidNoSmaller(area));
    };

    /* ---- the foot ---- */
    const stale = inDraft && (draft.stale || optimistic !== null);
    const nameMissing = !business?.businessName.trim();
    // The same two refusals the server gives on create, shown before the click.
    // Every mode with a door needs a place: a showroom is Both's outlet.
    const needsPlaces = business?.buy === "visit" || business?.buy === "both" || business?.buy === "we_go_to_them";
    const outletMissing = needsPlaces && !business?.outlets.some((o) => o.name.trim());
    const sellsMissing = !business?.services.some((s) => s.name.trim());
    const marketMissing = business?.buy === "online" && !(business.markets ?? []).length;
    const errorRef = useRef<HTMLDivElement>(null);

    const onDraft = useCallback(async () => {
        const res = await createDraft();
        if (!res) return;
        if (res.kind === "exists" && res.data?.draftId) {
            openDraft(res.data.draftId);
            return;
        }
        setFailure(res.kind === "network" ? { ...res, message: FRAME.network } : res);
        requestAnimationFrame(() => errorRef.current?.scrollIntoView({ block: "center" }));
    }, [createDraft, openDraft]);

    const markSeen = flow.markBusinessSeen;
    const onUpdate = useCallback(async () => {
        const res = await ctl.rebuild();
        if (!res) {
            markSeen();
            goTo("review");
            return;
        }
        if (res.code === "recipe_change_pending") setFailure({ ...res, message: COPY.recipePending });
        else if (res.kind !== "stale") setFailure(res);
    }, [ctl, goTo, markSeen]);

    useEffect(() => {
        if (!inDraft) {
            const blocked = unconfirmed.length > 0 || townAnchors.length > 0 || hasMismatch || nameMissing || outletMissing || marketMissing || sellsMissing;
            foot.set({
                back: flow.canReach("website") ? { onClick: () => goTo("website") } : null,
                status: unconfirmed.length || townAnchors.length || hasMismatch
                    ? COPY.foot.answerFirst
                    : nameMissing
                      ? COPY.foot.nameFirst
                      : outletMissing
                        ? COPY.foot.outletFirst(business?.buy === "both")
                        : marketMissing
                          ? COPY.foot.marketFirst
                          : sellsMissing
                            ? COPY.foot.sellsFirst(business?.buy === "online")
                            : COPY.foot.cost,
                primary: {
                    label: COPY.foot.draft,
                    icon: ArrowRightIcon,
                    disabled: blocked || !business,
                    busy: creating,
                    onClick: () => void onDraft(),
                },
            });
            return;
        }
        foot.set({
            status: stale && editable ? (townAnchors.length ? COPY.foot.answerFirst : marketMissing ? COPY.foot.marketFirst : COPY.foot.cost) : null,
            primary:
                stale && editable
                    ? {
                          label: COPY.foot.update,
                          icon: RefreshCwIcon,
                          disabled: nameMissing || marketMissing || townAnchors.length > 0,
                          busy: ctl.busy === "Drafting",
                          onClick: () => void onUpdate(),
                      }
                    : !flow.businessSeen && draft?.status === "ready"
                      ? {
                            // First AI check, first look: confirming is what ticks Business on the track.
                            label: COPY.foot.looksRight,
                            icon: ArrowRightIcon,
                            onClick: () => {
                                markSeen();
                                goTo("review");
                            },
                        }
                      : { label: COPY.foot.backToReview, icon: ArrowRightIcon, onClick: () => goTo("review") },
        });
    }, [inDraft, unconfirmed.length, townAnchors.length, hasMismatch, nameMissing, outletMissing, marketMissing, sellsMissing, business, creating, onDraft, onUpdate, stale, editable, ctl.busy, foot, flow, goTo, markSeen, draft?.status]);

    if (!business) {
        return (
            <>
                <StepHead title={COPY.title} />
                <Refusal>{FRAME.readFirst}</Refusal>
            </>
        );
    }

    /* ---- the sentence ---- */
    const gscOn = flow.gsc.status === "connected" || Boolean(draft?.search.connected);
    const domain = draft?.domain ?? site.normalized;
    let sentence: ReactNode;
    // The checks by name, so the sentence says what they are, not only how many.
    const localChecks = [
        ...unconfirmed.map(({ o }) => COPY.unconfirmedItem(o.name.trim())),
        ...townAnchors.map(({ o }) => COPY.anchorItem(o.name.trim())),
    ];
    const checkList = first && draft ? [...formThingsOpenTitles(draft), ...localChecks] : kind === "new_scan" ? localChecks : [];
    if (first && draft) {
        const checks = checkList;
        const open = checks.length;
        const day = pitchDay(draft);
        const contact = firstNameOf(draft.firstCheck?.contactName) || COPY.theClient;
        sentence = (
            <>
                {COPY.fromSigning(day, contact)}
                {open ? (
                    <b className="font-semibold text-gray-900">{COPY.toCheckList(checks.length)}</b>
                ) : stale ? (
                    COPY.staleSentence
                ) : day ? (
                    COPY.matchesPitch
                ) : (
                    COPY.nothingToCheck
                )}
            </>
        );
    } else if (kind === "new_scan") {
        sentence = (
            <>
                {COPY.readFrom(domain, gscOn)}
                {localChecks.length ? (
                    <b className="font-semibold text-gray-900">{COPY.toCheckList(localChecks.length)}</b>
                ) : stale ? COPY.staleSentence : !inDraft && detected?.confidence === "low" ? COPY.lowRead : COPY.allRight}
            </>
        );
    } else {
        sentence = stale ? COPY.staleSentence : COPY.editSentence;
    }

    const flagshipName = business.outlets[0]?.name ?? "the flagship";
    const oneVenue = isLocationMode(business);
    const placesLabel =
        business.buy === "we_go_to_them" ? COPY.areas : oneVenue ? COPY.location : COPY.outlets;
    // "Separate venues" answers the question for this list; a changed list may ask again.
    const sameVenue = sameVenueCandidates(business);
    const sameVenueKey = sameVenue
        ? JSON.stringify([business.category.trim().toLowerCase(), business.outlets.map((o) => [o.name.trim().toLowerCase(), o.town.trim().toLowerCase(), o.source])])
        : null;
    const listSameVenue = (names: string[]) => (names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0] ?? "");

    const topRows = (
        <>
                        <DefRow label={COPY.business}>
                            <div className="flex min-h-8 flex-wrap items-center gap-x-1 gap-y-1">
                                <InlineText
                                    value={business.businessName}
                                    label={COPY.name}
                                    empty={COPY.addName}
                                    disabled={!editable}
                                    strong
                                    required
                                    onCommit={(v) => update({ businessName: v })}
                                />
                                <span aria-hidden className="text-gray-400">
                                    ·
                                </span>
                                <InlineText
                                    value={business.category}
                                    label={COPY.category}
                                    empty={COPY.addCategory}
                                    disabled={!editable}
                                    onCommit={(v) => update({ category: v })}
                                />
                            </div>
                        </DefRow>

                        <DefRow
                            label={COPY.customers}
                            labelId="newscan-customers"
                            hintId="newscan-customers-mode"
                            hint={oneVenue ? COPY.locationHint : COPY.buyHint[business.buy]}
                        >
                            <RadioSeg<BuyMode>
                                options={BUY_ORDER.map((k) => ({ key: k, label: BUY_LABEL[k], icon: BUY_ICON[k], suggested: suggest === k }))}
                                value={business.buy}
                                onChange={setBuy}
                                labelledBy="newscan-customers"
                                describedBy={suggest ? "newscan-customers-hint newscan-customers-mode" : "newscan-customers-mode"}
                                wrapOnPhone
                                disabled={!editable}
                            />
                            {suggest && (
                                <Hint id="newscan-customers-hint" icon={business.buy === suggest ? GlobeIcon : PencilIcon}>
                                    {business.buy === suggest
                                        ? detected?.buyWhy
                                            ? COPY.suggested(detected.buyWhy)
                                            : null
                                        : COPY.changed(BUY_LABEL[suggest])}
                                </Hint>
                            )}
                        </DefRow>

                        {business.buy !== "online" && (
                            <DefRow label={placesLabel} hint={business.buy === "we_go_to_them" ? COPY.areasHint : undefined}>
                                <OutletList
                                    outlets={business.outlets}
                                    onChange={setOutlets}
                                    city={business.city}
                                    flagship={!oneVenue}
                                    location={oneVenue}
                                    noun={business.buy === "we_go_to_them" ? "area" : business.buy === "both" ? "showroom" : "outlet"}
                                    disabled={!editable}
                                    known={detected?.outlets.map((o) => o.name)}
                                    intent={outletIntent}
                                />
                                {sameVenue && editable && sameVenueKey !== keptSeparate && (
                                    <div role="group" aria-label={COPY.sameVenueMerge} className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl bg-amber-50 px-3 py-2">
                                        <span className={cn(TYPE.tc, "min-w-0 text-gray-800")}>
                                            {COPY.sameVenue(listSameVenue(sameVenue.map((o) => o.name)), business.outlets[0].town)}
                                        </span>
                                        <PillButton variant="text" onClick={() => update({ outlets: mergeIntoFirst(business.outlets) })}>
                                            {COPY.sameVenueMerge}
                                        </PillButton>
                                        <PillButton variant="text" onClick={() => setKeptSeparate(sameVenueKey)}>
                                            {COPY.sameVenueKeep}
                                        </PillButton>
                                    </div>
                                )}
                            </DefRow>
                        )}

                        {brand && (
                            <>
                                <DefRow label={COPY.markets} labelId="newscan-markets">
                                    <MarketList
                                        markets={business.markets ?? []}
                                        onChange={setMarkets}
                                        labelledBy="newscan-markets"
                                        disabled={!editable}
                                    />
                                </DefRow>
                                <DefRow
                                    label={COPY.toBusinesses}
                                    labelId="newscan-b2b"
                                    hintId="newscan-b2b-hint"
                                    hint={COPY.toBusinessesHint(Boolean(business.sellsToBusinesses))}
                                >
                                    <RadioSeg<"yes" | "no">
                                        options={[
                                            { key: "yes", label: COPY.yes, suggested: detected?.sellsToBusinessesGuess === true && suggestBrand },
                                            { key: "no", label: COPY.no, suggested: detected?.sellsToBusinessesGuess === false && suggestBrand },
                                        ]}
                                        value={business.sellsToBusinesses ? "yes" : "no"}
                                        onChange={(k) => update({ sellsToBusinesses: k === "yes" })}
                                        labelledBy="newscan-b2b"
                                        describedBy="newscan-b2b-hint"
                                        disabled={!editable}
                                    />
                                </DefRow>
                                <DefRow label={COPY.partners} labelFor="newscan-partners" hintId="newscan-partners-hint" hint={COPY.partnersHint}>
                                    <TagInput
                                        id="newscan-partners"
                                        describedBy="newscan-partners-hint"
                                        values={business.partnerProducts ?? []}
                                        onChange={(names) => update({ partnerProducts: names })}
                                        placeholder={COPY.partnersPlaceholder}
                                        disabled={!editable}
                                    />
                                </DefRow>
                            </>
                        )}

        </>
    );
    const bottomRows = (
        <>
                        <DefRow label={COPY.sells} labelFor="newscan-sells" hintId="newscan-sells-hint" hint={COPY.sellsHint}>
                            <TagInput
                                id="newscan-sells"
                                describedBy="newscan-sells-hint"
                                values={business.services.map((s) => s.name)}
                                onChange={(names) => {
                                    const was = new Map(business.services.map((s) => [s.name.toLowerCase(), s]));
                                    update({ services: names.map((name) => was.get(name.toLowerCase()) ?? { name, source: "staff" }) });
                                }}
                                // A First AI check says where the chips came from in one line under them, in words.
                                marked={first ? undefined : (_, i) => business.services[i]?.source === "staff"}
                                placeholder={COPY.sellsPlaceholder(brand)}
                                disabled={!editable}
                            />
                            {first && servicesFrom(business.services) && (
                                <Muted as="p" tone="ink4" size="tm" className="mt-1.5">{servicesFrom(business.services)}</Muted>
                            )}
                        </DefRow>

                        <DefRow label={COPY.recipe}>
                            <p className={cn(TYPE.tb, "m-0 py-1.5 text-gray-900")}>
                                <span className="font-medium">
                                    {inDraft && draft.recipe.shape === business.buy && (!brand || brandShapeFromDraft(draft) !== null)
                                        ? draft.recipe.label
                                        : RECIPE_LABEL[business.buy]}
                                </span>
                                <span className="tabular-nums text-gray-500">{` · ${shape}`}</span>
                            </p>
                        </DefRow>
        </>
    );
    const more = (
        <>

                <MoreOptions
                    languages={business.languages}
                    rivals={business.rivals}
                    otherNames={business.otherNames}
                    onChange={update}
                    disabled={!editable}
                    brand={brand}
                />

                <KnowledgeFold businessId={flow.businessId} domain={domain} formItems={first ? formItems : null} disabled={!editable && !inDraft} />

                {brand && (
                    <DrawerFold id="newscan.business.whynot" label={COPY.whyNotLocal} note={COPY.whyNotNote} remember>
                        <ul role="list" className="list-plain m-0 divide-y divide-gray-100 p-0">
                            {todaysRecipeOnStore(business)
                                .slice(0, 3)
                                .map((r) => (
                                    <li key={r.q} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 py-2.5 first:pt-1">
                                        <span className={cn(TYPE.tb, "min-w-0 text-gray-900")}>{r.q}</span>
                                        <Muted>{r.why}</Muted>
                                    </li>
                                ))}
                        </ul>
                    </DrawerFold>
                )}

                <span aria-live="polite" className="sr-only">
                    {said}
                </span>

                {failure && (
                    <div ref={errorRef}>
                        <Refusal>{failure.message}</Refusal>
                    </div>
                )}

        </>
    );

    if (split) {
        // First AI check: two panels that each scroll from lg up (the bars stay put): what to check and
        // where the 50 ask on the left, what they offer and the rest on the right (Jovi, 20 Oct).
        return (
            <div className="flex flex-col gap-6 lg:grid lg:h-full lg:grid-cols-2 lg:gap-0">
                <div className="min-w-0 space-y-3 lg:min-h-0 lg:overflow-y-auto lg:overscroll-contain lg:py-6 lg:pr-8">
            <StepHead title={COPY.title} sentence={sentence} />
            {/* What the checks are, each in its own words: a count alone sends staff hunting (retest, 15 Oct). */}
            {checkList.length > 0 && <MismatchCard items={checkList} sources={first ? COPY.mismatchFirst(firstNameOf(draft?.firstCheck?.contactName) || COPY.theClient) : COPY.mismatchSite} />}


                {townAnchors.map(({ o, i, town }) => (
                    <AnchorTownCard
                        // By name: a renamed outlet is a new question.
                        key={`${o.name}-${i}`}
                        area={o.name.trim()}
                        town={town}
                        others={business.outlets.filter((_, j) => j !== i).map((x) => x.name)}
                        disabled={!editable}
                        onUseArea={(sub) => takeSubArea(i, town, sub)}
                        onNoSmallerArea={(ring) => keepTownArea(i, ring)}
                    />
                ))}

                {unconfirmed.map(({ o, i }) => {
                    const { k, rest } = outletStakes(business.outlets, i);
                    return (
                        <div key={`${o.name}-${i}`} data-outlet-card="">
                            <DecisionCard
                                severity="decide"
                                title={COPY.outletTitle(o.name)}
                                detail={COPY.outletDetail(k, flagshipName === o.name ? business.outlets[1]?.name ?? flagshipName : flagshipName, rest)}
                                actions={[
                                    {
                                        key: "keep",
                                        label: COPY.keep,
                                        kind: "primary",
                                        onClick: () => answerOutlet(i, true),
                                    },
                                    {
                                        key: "leave",
                                        label: COPY.leaveOut,
                                        kind: "secondary",
                                        onClick: () => answerOutlet(i, false),
                                    },
                                ]}
                            />
                        </div>
                    );
                })}

                {mismatch && (
                    <div data-mismatch-card="">
                        <DecisionCard
                            severity="decide"
                            title={mismatch.kind === "door" ? COPY.doorTitle(mismatch.place, mismatch.to === "both") : COPY.noDoorTitle}
                            detail={mismatch.kind === "door" ? COPY.doorDetail(BUY_LABEL[mismatch.to], mismatch.to === "both") : COPY.noDoorDetail}
                            actions={
                                mismatch.kind === "door"
                                    ? [
                                          { key: "use", label: COPY.takeIt(BUY_LABEL[mismatch.to]), kind: "primary", onClick: () => answerMismatch(mismatch.to) },
                                          { key: "keep", label: COPY.doorKeep, kind: "text", onClick: () => answerMismatch(null) },
                                      ]
                                    : [
                                          { key: "use", label: COPY.takeIt(BUY_LABEL.online), kind: "primary", onClick: () => answerMismatch("online") },
                                          { key: "keep", label: COPY.noDoorKeep(BUY_LABEL[mismatch.from]), kind: "text", onClick: () => answerMismatch(null) },
                                      ]
                            }
                        />
                    </div>
                )}

                {inDraft && !editable && (
                    // Said up front, with the way out: a faded form alone read as broken (Jovi, 20 Oct).
                    <div role="status" className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl bg-blue-50 px-4 py-3 ring-1 ring-inset ring-blue-100">
                        {draft.status === "building" ? (
                            <Loader2Icon aria-hidden size={16} className="shrink-0 text-blue-600 motion-safe:animate-spin" />
                        ) : (
                            <LockIcon aria-hidden size={16} className="shrink-0 text-blue-600" />
                        )}
                        <p className={cn(TYPE.tb, "m-0 min-w-0 flex-1 text-gray-900")}>{draft.status === "building" ? COPY.building : draft.status === "approved" ? COPY.frozen : COPY.started}</p>
                        {draft.status === "approved" && (
                            <PillButton
                                size="sm"
                                busy={ctl.busy === "Reopening"}
                                disabled={ctl.busy !== null && ctl.busy !== "Reopening"}
                                onClick={() => void ctl.reopen()}
                            >
                                {COPY.reopen}
                            </PillButton>
                        )}
                    </div>
                )}

                <Surface>
                    <dl className="m-0">{topRows}</dl>
                </Surface>
                </div>
                <div className="min-w-0 space-y-3 lg:-mr-8 lg:min-h-0 lg:overflow-y-auto lg:overscroll-contain lg:border-l lg:border-gray-200 lg:px-8 lg:py-6">
                    <Surface>
                        <dl className="m-0">{bottomRows}</dl>
                    </Surface>
                    {more}
                </div>
            </div>
        );
    }

    return (
        <>
            <StepHead title={COPY.title} sentence={sentence} />
            {/* What the checks are, each in its own words: a count alone sends staff hunting (retest, 15 Oct). */}
            {checkList.length > 0 && <MismatchCard items={checkList} sources={first ? COPY.mismatchFirst(firstNameOf(draft?.firstCheck?.contactName) || COPY.theClient) : COPY.mismatchSite} />}

            <div className="space-y-3">
                {townAnchors.map(({ o, i, town }) => (
                    <AnchorTownCard
                        // By name: a renamed outlet is a new question.
                        key={`${o.name}-${i}`}
                        area={o.name.trim()}
                        town={town}
                        others={business.outlets.filter((_, j) => j !== i).map((x) => x.name)}
                        disabled={!editable}
                        onUseArea={(sub) => takeSubArea(i, town, sub)}
                        onNoSmallerArea={(ring) => keepTownArea(i, ring)}
                    />
                ))}

                {unconfirmed.map(({ o, i }) => {
                    const { k, rest } = outletStakes(business.outlets, i);
                    return (
                        <div key={`${o.name}-${i}`} data-outlet-card="">
                            <DecisionCard
                                severity="decide"
                                title={COPY.outletTitle(o.name)}
                                detail={COPY.outletDetail(k, flagshipName === o.name ? business.outlets[1]?.name ?? flagshipName : flagshipName, rest)}
                                actions={[
                                    {
                                        key: "keep",
                                        label: COPY.keep,
                                        kind: "primary",
                                        onClick: () => answerOutlet(i, true),
                                    },
                                    {
                                        key: "leave",
                                        label: COPY.leaveOut,
                                        kind: "secondary",
                                        onClick: () => answerOutlet(i, false),
                                    },
                                ]}
                            />
                        </div>
                    );
                })}

                {mismatch && (
                    <div data-mismatch-card="">
                        <DecisionCard
                            severity="decide"
                            title={mismatch.kind === "door" ? COPY.doorTitle(mismatch.place, mismatch.to === "both") : COPY.noDoorTitle}
                            detail={mismatch.kind === "door" ? COPY.doorDetail(BUY_LABEL[mismatch.to], mismatch.to === "both") : COPY.noDoorDetail}
                            actions={
                                mismatch.kind === "door"
                                    ? [
                                          { key: "use", label: COPY.takeIt(BUY_LABEL[mismatch.to]), kind: "primary", onClick: () => answerMismatch(mismatch.to) },
                                          { key: "keep", label: COPY.doorKeep, kind: "text", onClick: () => answerMismatch(null) },
                                      ]
                                    : [
                                          { key: "use", label: COPY.takeIt(BUY_LABEL.online), kind: "primary", onClick: () => answerMismatch("online") },
                                          { key: "keep", label: COPY.noDoorKeep(BUY_LABEL[mismatch.from]), kind: "text", onClick: () => answerMismatch(null) },
                                      ]
                            }
                        />
                    </div>
                )}

                {inDraft && !editable && (
                    // Said up front, with the way out: a faded form alone read as broken (Jovi, 20 Oct).
                    <div role="status" className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl bg-blue-50 px-4 py-3 ring-1 ring-inset ring-blue-100">
                        {draft.status === "building" ? (
                            <Loader2Icon aria-hidden size={16} className="shrink-0 text-blue-600 motion-safe:animate-spin" />
                        ) : (
                            <LockIcon aria-hidden size={16} className="shrink-0 text-blue-600" />
                        )}
                        <p className={cn(TYPE.tb, "m-0 min-w-0 flex-1 text-gray-900")}>{draft.status === "building" ? COPY.building : draft.status === "approved" ? COPY.frozen : COPY.started}</p>
                        {draft.status === "approved" && (
                            <PillButton
                                size="sm"
                                busy={ctl.busy === "Reopening"}
                                disabled={ctl.busy !== null && ctl.busy !== "Reopening"}
                                onClick={() => void ctl.reopen()}
                            >
                                {COPY.reopen}
                            </PillButton>
                        )}
                    </div>
                )}

                <Surface>
                    <dl className="m-0">
                        {topRows}
                        {bottomRows}
                    </dl>
                </Surface>
                {more}
            </div>
        </>
    );
}

/* ------------------------------------------------------------------------- *
 * Pieces
 * ------------------------------------------------------------------------- */

/** "3 from the pitch · 1 from the client's form": where a First AI check's services came from, in the outlet rows' words. */
function servicesFrom(services: ReadonlyArray<{ source?: string | null }>): string {
    const order = ["scan", "form", "quote", "site", "staff"] as const;
    const counts = new Map<string, number>();
    services.forEach((x) => {
        const src = x.source && x.source in ITEM_SOURCE ? x.source : "site";
        counts.set(src, (counts.get(src) ?? 0) + 1);
    });
    return order
        .filter((k) => (counts.get(k) ?? 0) > 0)
        .map((k) => `${counts.get(k)} ${ITEM_SOURCE[k].label.charAt(0).toLowerCase()}${ITEM_SOURCE[k].label.slice(1)}`)
        .join(" · ");
}

/** The checks that disagree, as one card above the form: the sources compared, then each mismatch (Jovi, 20 Oct). */
function MismatchCard({ items, sources }: { items: string[]; sources: string }) {
    return (
        <section aria-labelledby="newscan-mismatch" className="mb-5 flex items-start gap-3 rounded-2xl bg-amber-50 p-4 ring-1 ring-inset ring-amber-200">
            <TriangleAlertIcon aria-hidden size={18} className="mt-0.5 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
                <h2 id="newscan-mismatch" className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>
                    {COPY.mismatchTitle}
                    <span className="ml-2 font-normal tabular-nums text-amber-700">{items.length}</span>
                </h2>
                <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-600")}>{sources}</p>
                <ul role="list" className="list-plain m-0 mt-3 space-y-1.5 p-0">
                    {items.map((c) => (
                        <li key={c} className={cn(TYPE.tb, "flex items-start gap-2 text-gray-900")}>
                            <span aria-hidden className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                            <span className="min-w-0">{c}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
}

function Hint({ id, icon: Icon, children }: { id?: string; icon?: typeof GlobeIcon; children: ReactNode }) {
    if (!children) return null;
    return (
        <Muted as="p" id={id} className="flex items-start gap-1.5">
            {Icon && <Icon aria-hidden size={12} className="mt-[3px] shrink-0 text-gray-400" />}
            <span className="min-w-0">{children}</span>
        </Muted>
    );
}
